import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '@/components/@IntranetLibrary/Select';
import {
  ApprovalFormTable,
  ApprovalTableAddRowButton,
  ApprovalTableCellInput,
  ApprovalTableRow,
  ApprovalTableRowDeleteButton,
  approvalTableCellSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import { useDialog } from '@/hooks/global-tools';
import PlusIcon from '@/public/icons/approval/plus.svg';
import {
  createEntExpenseRow,
  getKstarItems,
  type EntBudgetCategory,
  type EntExpenseRow,
} from '@/utils/approval/entExpense';
import { formatAmountInput } from '@/utils/approval/expensePreApproval';

type DetailTableProps = {
  rows: EntExpenseRow[];
  categories: EntBudgetCategory[];
  onChange: (rows: EntExpenseRow[]) => void;
  /** 사용자가 분류를 직접 고르면 AI 미매칭 안내를 닫음 */
  onCategoryPick: () => void;
};

/** 삭제 확인은 입력칸(비용·비고)에 값이 있을 때만 */
const hasInputValue = (row: EntExpenseRow) =>
  row.cost.trim() !== '' || row.remark.trim() !== '';

const DetailTable = ({
  rows,
  categories,
  onChange,
  onCategoryPick,
}: DetailTableProps) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [hoverRowId, setHoverRowId] = useState<string | null>(null);

  const patch = (id: string, next: Partial<EntExpenseRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...next } : r)));

  const remove = (row: EntExpenseRow) => {
    const doRemove = () => onChange(rows.filter((r) => r.id !== row.id));
    if (!hasInputValue(row)) {
      doRemove();
      return;
    }
    dialog.show({
      message: t(
        '해당 항목을 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.',
      ),
      confirmButtonText: t('삭제'),
      cancelButtonText: t('취소'),
      onConfirm: doRemove,
    });
  };

  const categoryOptions = categories.map((c) => ({
    value: c.code,
    label: c.label,
  }));
  const canDelete = rows.length > 1;

  return (
    <div className="additional-table">
      <ApprovalFormTable editable className="ent-detail-table">
        <colgroup>
          <col style={{ width: 202 }} />
          <col style={{ width: 202 }} />
          <col style={{ width: 140 }} />
          <col style={{ width: 202 }} />
          <col style={{ width: 32 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="align-left">{t('분류')}</th>
            <th className="align-left">{t('항목')}</th>
            <th className="align-right">{t('비용')}</th>
            <th className="align-left">{t('비고')}</th>
            <th aria-label={t('삭제')} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const items = row.category ? getKstarItems(row.category) : [];
            return (
              <ApprovalTableRow
                key={row.id}
                deleteHover={canDelete && hoverRowId === row.id}
                data-row-id={row.id}
              >
                <td data-field="category">
                  <Select
                    value={row.category}
                    placeholder={t('분류 선택')}
                    options={
                      row.category &&
                      !categoryOptions.some((o) => o.value === row.category)
                        ? [
                            ...categoryOptions,
                            { value: row.category, label: row.categoryLabel },
                          ]
                        : categoryOptions
                    }
                    onChange={(e) => {
                      const code = String(e.target.value);
                      onCategoryPick();
                      patch(row.id, {
                        category: code,
                        categoryLabel:
                          categories.find((c) => c.code === code)?.label ?? '',
                        item: '',
                      });
                    }}
                    sx={approvalTableCellSelectSx}
                  />
                </td>
                <td data-field="item">
                  <Select
                    value={row.item}
                    placeholder={t('항목 선택')}
                    disabled={items.length === 0}
                    options={items.map((item) => ({
                      value: item,
                      label: t(item, { context: 'ent' }),
                    }))}
                    onChange={(e) =>
                      patch(row.id, { item: String(e.target.value) })
                    }
                    sx={approvalTableCellSelectSx}
                  />
                </td>
                <td>
                  <ApprovalTableCellInput
                    name="cost"
                    className="align-right"
                    inputProps={{ inputMode: 'decimal' }}
                    value={row.cost}
                    onChange={(e) =>
                      patch(row.id, { cost: formatAmountInput(e.target.value) })
                    }
                  />
                </td>
                <td>
                  <ApprovalTableCellInput
                    name="remark"
                    inputProps={{ maxLength: 100 }}
                    value={row.remark}
                    onChange={(e) => patch(row.id, { remark: e.target.value })}
                  />
                </td>
                <td className="align-center">
                  <ApprovalTableRowDeleteButton
                    type="button"
                    aria-label={t('삭제')}
                    disabled={!canDelete}
                    onMouseEnter={() => setHoverRowId(row.id)}
                    onMouseLeave={() => setHoverRowId(null)}
                    onClick={() => remove(row)}
                  />
                </td>
              </ApprovalTableRow>
            );
          })}
        </tbody>
      </ApprovalFormTable>
      <ApprovalTableAddRowButton
        type="button"
        onClick={() => onChange([...rows, createEntExpenseRow()])}
      >
        <PlusIcon />
        {t('항목 추가')}
      </ApprovalTableAddRowButton>
    </div>
  );
};

export default DetailTable;
